import { Controller, useFormContext, get, useWatch } from "react-hook-form"
import { CustomSX, Dependency, IFieldContext, ValidFieldOperation } from "../../../../../interface/FormGenerator"
import { Box, TextField as MUITextField, SxProps, TextFieldProps, Typography } from "@mui/material"
import { COLORS } from "@/theme/localTheme"
import { useEffect, useMemo } from "react"
import { useContext } from "react"
import FieldContext from "../../FieldContext"
import { ErrorSX } from "../../Styles/CommonStyles.sx"
import { validateRegex } from "../CJS"

const TextFieldSX: CustomSX = {
  enable: {
    "& .MuiOutlinedInput-input": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& fieldset": {
      borderColor: COLORS?.adminAppOutlineColor,
    },

    "& .MuiInputBase-root.Mui-focused:not(.Mui-error) fieldset": {
      "&:hover fieldset": {
        borderColor: COLORS?.adminAppOutlineColor,
      },
      borderColor: COLORS?.adminAppOutlineColor,
    },
    "& .MuiInputBase-root:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline ": {
      borderColor: COLORS?.adminAppOutlineColor,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiOutlinedInput-root:has(.Mui-error) fieldset": {
      borderColor: "red",
    },
  },
  disable: {
    "& .MuiOutlinedInput-input": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& fieldset": {
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "&:hover fieldset": {
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "& .MuiInputBase-root.Mui-focused fieldset": {
      "&:hover fieldset": {
        borderColor: COLORS?.adminAppTextPrimary,
      },
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline ": {
      borderColor: COLORS?.adminAppTextPrimary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
  common: {
    '& .MuiInputBase-root[prefix="$"] input': {
      paddingLeft: "5px",
    },
    "& .MuiInputBase-root[data-rtl] input": {
      textAlign: "right",
    },
  },
}

function calculation(values: unknown[], operation: ValidFieldOperation): unknown {
  let value = 0
  switch (operation) {
    case "add":
      value = values.reduce((acc, curr) => Number(acc) + Number(curr)) as number
      return value
    case "product":
      value = values.reduce((acc, curr) => Number(acc) * Number(curr)) as number
      return value
    default:
      return value
  }
}

function useNormalizeFieldDependencies(dependency?: Dependency) {
  const values = useWatch({ name: dependency?.keys || [] })

  const valueDefined = useMemo(() => {
    if (!dependency?.relation) return values[0]
    else if (dependency.relation) {
      return calculation(values, dependency.relation)
    }
    return ""
  }, [values])

  return { value: valueDefined }
}

const TextField = (props?: Partial<IFieldContext & TextFieldProps>) => {
  const { control,  setValue, formState, } = useFormContext()
  const { errors } = formState
  const contextData = useContext(FieldContext)

  const { formKey, form, onChange: onValueChange, ...rest } = Object.keys(contextData).length > 0 ? contextData : (props as IFieldContext)

  const error = get(errors, formKey)

  if (!form) throw new Error("Missing Parameter: Field Context")

  if (!formKey) throw new Error("Missing Parameter: Form Key")

  const isNumberField = form.input_type === "number"
  let inputProps = {}
  if (isNumberField) {
    inputProps = {
      ...inputProps,
      "data-rtl": form.rtl,
      // inputProps: { min: 0 },
    }
  }

  const { value } = useNormalizeFieldDependencies(form.dependency)

  useEffect(() => {
    if (value) {
      setValue(form.keyword, value)
    }
  }, [value])

  return (
    <>
      <Controller
        name={formKey}
        defaultValue={form?.answer_value ?? ""}
        control={control}
        rules={{
          required: typeof form.required === "boolean" && form.required ? "Required" : form.required,
          validate: (value) => {
            if (!form.required && !value) return undefined
            if (form.regex) {
              return validateRegex(value, form.regex)
            } else return undefined
          },
        }}
        render={({ field: { ref, ...fields } }) => (
          <MUITextField
            {...fields}
            fullWidth
            sx={[{ ...(form.can_edit ? TextFieldSX.enable : TextFieldSX.disable) }, { ...TextFieldSX.common }] as SxProps}
            label={form.labelPos ? "" : form.label}
            type={form.input_type}
            InputProps={{
              ...inputProps,
              readOnly: !form.can_edit, //
              startAdornment: form.prefix,
              prefix: form.prefix,
              inputRef: ref,
            }}
            error={error?.message}
            {...rest}
          />
        )}
      />
      {((typeof form.required === "string" && form.required) || form.regex) && error?.message && (
        <Box className="ErrorVai" sx={ErrorSX}>
          <Typography fontSize={"0.75rem"}>{error?.message}</Typography>
        </Box>
      )}
    </>
  )
}

export default TextField
